import React, { useRef } from 'react'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { RegistroGrafico } from './Dashboard'
import { useDismissTooltipOnOutsideTouch } from '../../utils/hooks/useDismissTooltipOnOutsideTouch'

const C_LD = '#5292D1'
const C_LE = '#E8A84C'

type Props = {
    data: RegistroGrafico[]
}

const fmtTime = (t: string) => new Date(t).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

const tooltipStyle = {
    background: '#1a2535', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
    fontFamily: 'Nunito,sans-serif', fontSize: 12, color: '#fff',
}

const axisTick = { fontSize: 11, fill: 'rgba(255,255,255,0.4)', fontFamily: 'Nunito, sans-serif' }

/** Máximo 2 casas decimais — mesmo padrão usado em ChartPanel.tsx (Charts.tsx). */
function fmt(v: number): string {
    if (isNaN(v)) return ''
    return Number.isInteger(v) ? String(v) : v.toFixed(2)
}

/** Mesmo fator de padding "moderado" (10%) usado por padrão em Charts.tsx (getAxisDomain). */
function getPaddedDomain(values: number[]): ['auto' | number, 'auto' | number] {
    const finite = values.filter(isFinite)
    if (finite.length < 2) return ['auto', 'auto']

    const dataMin = Math.min(...finite)
    const dataMax = Math.max(...finite)
    const range = dataMax - dataMin
    if (range === 0) return ['auto', 'auto']

    const pad = 0.10 * range
    return [
        parseFloat((dataMin - pad).toFixed(4)),
        parseFloat((dataMax + pad).toFixed(4)),
    ]
}

/** Mesma paleta/estilo visual do ChartPanel.tsx (Charts.tsx), sem grid arrastável/zoom — tela fixa. */
const SimpleTernoChart: React.FC<Props> = ({ data }) => {
    const pressaoDomain = getPaddedDomain(data.flatMap(d => [d.pressao_ld, d.pressao_le]))
    const deslocamentoDomain = getPaddedDomain(data.flatMap(d => [d.deslocamento_ld, d.deslocamento_le]))
    const containerRef = useRef<HTMLDivElement>(null)
    useDismissTooltipOnOutsideTouch(containerRef)

    return (
        <div ref={containerRef}>
            <div style={{ height: 90 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="timestamp" tickFormatter={fmtTime} tick={axisTick} axisLine={false} tickLine={false} />
                        <YAxis domain={pressaoDomain} tickFormatter={fmt} tick={axisTick} axisLine={false} tickLine={false} width={56} />
                        <Tooltip contentStyle={tooltipStyle} labelFormatter={(label) => fmtTime(String(label))} formatter={(v) => fmt(Number(v))} />
                        <Line type="monotone" dataKey="pressao_ld" name="LD (psi)" stroke={C_LD} strokeWidth={2} dot={false} isAnimationActive={false} />
                        <Line type="monotone" dataKey="pressao_le" name="LE (psi)" stroke={C_LE} strokeWidth={2} dot={false} isAnimationActive={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div style={{ height: 90, marginTop: 4 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="timestamp" tickFormatter={fmtTime} tick={axisTick} axisLine={false} tickLine={false} />
                        <YAxis domain={deslocamentoDomain} tickFormatter={fmt} tick={axisTick} axisLine={false} tickLine={false} width={56} />
                        <Tooltip contentStyle={tooltipStyle} labelFormatter={(label) => fmtTime(String(label))} formatter={(v) => fmt(Number(v))} />
                        <Line type="monotone" dataKey="deslocamento_ld" name="LD (mm)" stroke={C_LD} strokeWidth={2} dot={false} isAnimationActive={false} />
                        <Line type="monotone" dataKey="deslocamento_le" name="LE (mm)" stroke={C_LE} strokeWidth={2} dot={false} isAnimationActive={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}

export default SimpleTernoChart
